// Sugalarm — the optional "who's treating this?" step.
//
// ⚠ CYCLE 404 REMOVED THIS SHEET'S DURATION STEP. "I'm treating this" is
// now a SINGLE TAP that opens the window directly: there is one duration,
// ten minutes, and the server assigns it. Nobody standing over a hypo
// should be estimating durations, and a system that re-verifies every ten
// minutes is safer than a caregiver pre-buying fifteen. What remains is
// the cycle-392 name step, unchanged.
//
// ⚠ ONE REAL CONSEQUENCE, NAMED RATHER THAN GLOSSED: the duration step's
// Cancel went with it, because the tap IS the commit now — there is no
// longer a moment between opening the sheet and opening the window in
// which to back out. The window is still bounded, still breakable by any
// of the four escape hatches, the kiosk still cues every five minutes, and
// Snooze is still on the surface behind this. Arc cycle 3's "Resume
// alerts" restores a real undo.
//
// WHAT THIS SHEET IS NOT: it is not Stop, and it never becomes Stop. The
// urgent-low surface behind it keeps its Snooze, keeps its live value, and
// keeps `Stop` absent (the cycle-73 floor).
//
// THE ALARM CONTEXT STAYS ON SCREEN BEHIND IT — dimmed, never replaced.
// The room must not lose sight of the number while somebody answers an
// OPTIONAL question; that is why the scrim is a translucent fill over the
// alarm rather than an opaque page.

import { useEffect, useRef, useState } from "react";

import * as emergencyContactsApi from "../../api/emergencyContacts";
import type { EmergencyContact } from "../../lib/types";
import styles from "./TreatingSheet.module.css";

interface Props {
  /** Records the custodian. `null` is the explicit "Someone else". */
  onIdentity?: (contactId: string | null) => void;
  /** Closes the name step without recording anything. The window keeps
   *  running — dismissing a name prompt must never end treatment. */
  onDismissIdentity?: () => void;
}

export function TreatingSheet({ onIdentity, onDismissIdentity }: Props) {
  // Cycle 392: the verified contacts, in call order. Loaded only when the
  // name step is actually reached, so the duration step never waits on a
  // fetch — identity must never delay a caregiver.
  const [contacts, setContacts] = useState<EmergencyContact[] | null>(null);
  // Cycle 395 — the tile for the last caregiver who took custody on THIS
  // screen opens already selected.
  //
  // ⚠ A PRE-SELECTED TILE NEVER SENDS ON ITS OWN. This is state for a
  // border, nothing more: it is not read on dismiss, not read on the 45 s
  // self-dismiss, not read when the window breaks, and never posted
  // without an explicit tap. If the caregiver walks away the notice goes
  // out ANONYMOUS, because the platform does not assert things no human
  // said.
  const [preselected, setPreselected] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await emergencyContactsApi.list();
      if (cancelled || !r.ok) return;
      const verified = r.data.contacts
        .filter((c) => c.verified)
        .sort((a, b) => a.call_order - b.call_order);
      setContacts(verified);
      // Validated AGAIN at render, against the list actually on screen.
      // The server already scoped it to the member in scope; this second
      // check means an unverified or filtered-out contact can never end
      // up highlighted with no tile under it.
      const remembered = r.data.preselected_contact_id ?? null;
      setPreselected(
        remembered !== null && verified.some((c) => c.id === remembered)
          ? remembered
          : null,
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const firstRef = useRef<HTMLButtonElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Move focus in so the sheet is immediately keyboard-reachable, and
  // trap Tab inside it — the same posture every other modal surface in
  // the app takes (cycle 34.20 / 43 / 45).
  useEffect(() => {
    // Re-run on the step transition so the name step is immediately
    // keyboard-reachable without a stray Tab.
    const root = rootRef.current;
    const first =
      firstRef.current ??
      root?.querySelector<HTMLElement>("button:not([disabled])");
    first?.focus();
  }, [contacts, preselected]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        // Escape closes the STEP and leaves the window running —
        // dismissing a name prompt must never end treatment.
        onDismissIdentity?.();
        return;
      }
      if (e.key !== "Tab") return;
      const root = rootRef.current;
      if (!root) return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>("button:not([disabled])"),
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismissIdentity]);

  return (
      <div className={styles.scrim} role="presentation">
        <div
          ref={rootRef}
          className={styles.sheet}
          role="dialog"
          aria-modal="true"
          aria-labelledby="sg-treating-title"
        >
          <div id="sg-treating-title" className={styles.title}>
            Who&apos;s treating this?
          </div>
          <div className={styles.lede}>
            Optional. The window is already running either way.
          </div>

          {/* ⚠ SCROLLS INSIDE THE SHEET, WITH THE FOOTER PINNED BELOW IT.
              The sheet must not grow past its height budget, because the
              urgent-low banner and the live glucose value sit behind it —
              THE ROOM MUST NEVER LOSE SIGHT OF THE NUMBER. */}
          <div className={styles.contacts}>
            {(contacts ?? []).map((c) => {
              const isPreselected = c.id === preselected;
              return (
                <button
                  key={c.id}
                  // Focus the remembered tile so it is one Enter away for
                  // a keyboard, without changing what a tap does.
                  ref={isPreselected ? firstRef : undefined}
                  type="button"
                  className={
                    isPreselected
                      ? `${styles.contact} ${styles.contactSelected}`
                      : styles.contact
                  }
                  // The tile carries a SELECTED state, not a pressed one:
                  // nothing has been recorded until it is tapped.
                  aria-pressed={isPreselected || undefined}
                  onClick={() => onIdentity?.(c.id)}
                >
                  {/* No copy names the remembered person before they
                      confirm. The label is exactly what it always was;
                      only the selected state differs. */}
                  {c.label}
                </button>
              );
            })}
          </div>

          <div className={styles.identityFooter}>
            <button
              type="button"
              className={styles.option}
              onClick={() => onIdentity?.(null)}
            >
              Someone else
            </button>
            <button
              type="button"
              className={styles.cancel}
              onClick={() => onDismissIdentity?.()}
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    );
}
